"use client";

import React from "react";
import {
  TextFieldRenderer,
  TextareaFieldRenderer,
  NumberFieldRenderer,
  ProbabilitySliderFieldRenderer,
  BooleanFieldRenderer,
  DefaultFieldRenderer,
} from "./primitives";
import {
  SelectFieldRenderer,
  ClientSelectFieldRenderer,
  CompanySelectFieldRenderer,
  TicketSelectFieldRenderer,
  UserFieldRenderer,
  MultiUserFieldRenderer,
} from "./selects";
import {
  DateFieldRenderer,
  DatetimeFieldRenderer,
  TimeFieldRenderer,
  DueDateFieldRenderer,
  DateWorkedFieldRenderer,
} from "./dates";
import {
  TagsFieldRenderer,
  ArrayFieldRenderer,
  EmailAliasesFieldRenderer,
  PhoneAliasesFieldRenderer,
  TechnicalIdentifiersFieldRenderer,
  TicketIdFieldRenderer,
  TemplateSelectFieldRenderer,
} from "./complex";
import { OfferingLineItemsFormField } from "./OfferingLineItemsFormField";
import { AddressFieldRenderer, isFullCrmAddressForm } from "./address";
import type { FieldRenderContext } from "./context";
import { FieldHelpPopover } from "@/components/shared/FieldHelp/FieldHelpPopover";

/**
 * Dispatcher that routes to the appropriate field renderer based on field type.
 */
export function renderField(ctx: FieldRenderContext): React.ReactNode {
  const { fieldName, field, customComponents } = ctx;

  // Check for custom component first
  const CustomComponent = customComponents?.[fieldName];
  if (CustomComponent) {
    return (
      <div key={fieldName} className="space-y-2">
        <label htmlFor={fieldName} className="flex items-center gap-1.5">
          <span>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </span>
          {ctx.config?.entity ? (
            <FieldHelpPopover entity={ctx.config.entity} fieldId={fieldName} side="right" align="start" />
          ) : null}
        </label>
        <CustomComponent {...ctx} className={ctx.errors[fieldName] ? "border-red-500" : ""} />
        {ctx.errors[fieldName] && (
          <p className="text-sm text-red-600">{ctx.errors[fieldName]?.message}</p>
        )}
      </div>
    );
  }

  // Special-case known fields
  if (fieldName === "email_aliases" && field.customComponent === "EmailAliasesEditor") {
    return <EmailAliasesFieldRenderer ctx={ctx} />;
  }

  if (fieldName === "phone_aliases" && field.customComponent === "PhoneAliasesEditor") {
    return <PhoneAliasesFieldRenderer ctx={ctx} />;
  }

  if (field.customComponent === "TechnicalIdentifiersEditor") {
    return <TechnicalIdentifiersFieldRenderer ctx={ctx} />;
  }

  if (fieldName === "ticket_id") {
    return <TicketIdFieldRenderer ctx={ctx} />;
  }

  // Address autocomplete, but only on a form that declares the whole address
  // set — otherwise the parsed city/state/ZIP would have nowhere to land. This
  // one branch covers create AND edit for contacts AND companies, because all
  // four modals render this form.
  if (fieldName === "address" && isFullCrmAddressForm(ctx)) {
    return <AddressFieldRenderer ctx={ctx} />;
  }

  // Probability slider for close_probability (same UX as deal table inline edit)
  if (
    fieldName === "close_probability" &&
    field.type === "number" &&
    field.validation?.min === 0 &&
    field.validation?.max === 100
  ) {
    return <ProbabilitySliderFieldRenderer ctx={ctx} />;
  }

  // JSON fields with lineItemsType (offerings staff/hard-cost)
  if (field.type === "json" && (field as { lineItemsType?: string }).lineItemsType) {
    const lineItemsType = (field as { lineItemsType: string }).lineItemsType;
    if (lineItemsType === "staff" || lineItemsType === "hard-cost") {
      return (
        <OfferingLineItemsFormField
          ctx={ctx}
          variant={lineItemsType === "staff" ? "staff" : "hard-cost"}
        />
      );
    }
  }

  // Route based on field type
  switch (field.type) {
    case "text":
      return <TextFieldRenderer ctx={ctx} />;
    case "textarea":
      return <TextareaFieldRenderer ctx={ctx} />;
    case "number":
      return <NumberFieldRenderer ctx={ctx} />;
    case "boolean":
      return <BooleanFieldRenderer ctx={ctx} />;
    case "select":
      return <SelectFieldRenderer ctx={ctx} />;
    case "client-select":
      return <ClientSelectFieldRenderer ctx={ctx} />;
    case "company-select":
      return <CompanySelectFieldRenderer ctx={ctx} />;
    case "ticket-select":
      return <TicketSelectFieldRenderer ctx={ctx} />;
    case "user":
      return <UserFieldRenderer ctx={ctx} />;
    case "multi-user":
      return <MultiUserFieldRenderer ctx={ctx} />;
    case "tags":
      return <TagsFieldRenderer ctx={ctx} />;
    case "array":
      return <ArrayFieldRenderer ctx={ctx} />;
    case "date":
      return <DateFieldRenderer ctx={ctx} />;
    case "datetime":
      return <DatetimeFieldRenderer ctx={ctx} />;
    case "time":
      return <TimeFieldRenderer ctx={ctx} />;
    case "due-date":
      return <DueDateFieldRenderer ctx={ctx} />;
    case "date-worked":
      return <DateWorkedFieldRenderer ctx={ctx} />;
    case "template-select":
      return <TemplateSelectFieldRenderer ctx={ctx} />;
    default:
      return <DefaultFieldRenderer ctx={ctx} />;
  }
}
